import React from 'react';
import { api } from '../api.js';
import * as T from '../time.js';
import { mapBookings, mapRooms } from '../mapping.js';
import { Empty, Icon, Lbl, Loading, Modal, PH, PageTabs, Panel } from './components.jsx';
import { applyServerData, ME } from './data.jsx';
import { Button, Checkbox, Field, Pill, RadioGroup, SegmentedControl } from './ds/fgcp-ds.jsx';
import { PeoplePicker } from './people-picker.jsx';
import { FacilityAdmin } from './booking-admin.jsx';
import { BookingDetail, MyBookings, PendingApprovals } from './booking-detail.jsx';

// booking.jsx — 회의실·설비 예약(계획 문서 5.2 2-3). 설비·분류·이용 요일·시간대·최대 길이는 서버 값이고(GET /facilities), 시간표의 범위와
// 예약 창의 선택지는 그 값으로 그린 미리보기다 — 예약할 수 있는지(지난 시각·이용 시간·최대 길이·정원·겹침)와 승인 대기 여부는 서버가 정한다.
// 탭: 예약 현황 · 내 예약 · 승인 대기(actions.approve) · 설비 관리(actions.manage). 예약 상세는 블록·목록·알림 링크(#/booking/<id>)로 연다.
// 반복 예약(2-3d): 예약 창의 '반복'(매일·매주+요일, 끝: 횟수·날짜) — 회차 수 미리보기는 서버 규칙과 같은 계산이고 판정은 서버, 겹치는 회차는
// 서버가 준 목록을 창에 보인다(아무것도 예약되지 않는다). 반복 전체 바꾸기는 제목·참석자만(시간·설비는 회차마다 '이날만 바꾸기').

const { useState: uS, useEffect: uE, useCallback: uCb } = React;
const WD = ['월', '화', '수', '목', '금', '토', '일'];
const COL_W = 92, LABEL_W = 116;
const pad = n => String(n).padStart(2, '0');
/** 소수 시각 → 'HH:MM'(24:00까지) */
const fmtH = v => pad(Math.floor(v)) + ':' + pad(Math.round((v % 1) * 60));
// 날짜는 (처음 연 때) 이번 달 1일부터 센 번호(idx)로 다룬다 — 달력 날짜 산술(time.js), 날짜·시각은 앱 시간대(계획 문서 5.3 3d).
// 기준 달은 화면을 처음 그릴 때 정한다(모듈을 읽을 때가 아니다 — 앱 시간대가 /me를 받은 뒤 정해진다)
let ANCHOR = null;
const anchorYmd = () => (ANCHOR ??= `${T.todayYmd().slice(0, 7)}-01`);
const todayIdx = () => T.daysBetween(anchorYmd(), T.todayYmd());
const ymdOf = idx => T.addDays(anchorYmd(), idx);
const dinfo = idx => { const day = ymdOf(idx); const { y, m, d } = T.splitYmd(day); return { y, m, d, wi: (T.dowOfYmd(day) + 6) % 7 }; };
const idxOf = ymd => T.daysBetween(anchorYmd(), ymd);
/** 날짜(idx)와 소수 시각 → ISO(앱 시간대의 벽시계) */
const isoAt = (idx, h) => T.zonedIso(ymdOf(idx), h);
/** 지금 시각(앱 시간대, 소수) */
const nowHour = () => T.hourOf(Date.now());
/** 그날 그 설비가 여는 시간대 [시작, 끝](소수 시각) — 쉬는 요일이면 null */
const windowOf = (room, wi) => (room.openDays.includes(wi + 1) ? [room.open, room.close] : null);
const allDay = room => room.open === 0 && room.close === 24;
const durationText = min => {
  const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
  return [d && `${d}일`, h && `${h}시간`, m && `${m}분`].filter(Boolean).join(' ');
};
const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5 };

function WeekStrip({ winStart, selIdx, onSelect, onShift, counts }) {
  const a = dinfo(winStart), b = dinfo(winStart + 13);
  const label = a.y === b.y && a.m === b.m ? `${a.y}년 ${a.m}월` : a.y === b.y ? `${a.y}년 ${a.m}–${b.m}월` : `${a.y}년 ${a.m}월 – ${b.y}년 ${b.m}월`;
  const canPrev = winStart > 0;
  return (
    <Panel>
      <div className="ph">
        <button className="navbtn" disabled={!canPrev} onClick={() => onShift(-7)} aria-label="이전 주" style={{ opacity: canPrev ? 1 : .35 }}><Icon name="chevron-left" size={14} /></button>
        <h3 style={{ margin: '0 auto' }}>{label}</h3>
        <button className="navbtn" onClick={() => onShift(7)} aria-label="다음 주"><Icon name="chevron-right" size={14} /></button>
      </div>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {[0, 1].map(r => (
          <div key={r} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 7 }}>
            {Array.from({ length: 7 }, (_, i) => winStart + r * 7 + i).map(idx => {
              const n = dinfo(idx);
              const isToday = idx === todayIdx(), isSel = idx === selIdx, past = idx < todayIdx();
              const cnt = counts[idx] ?? 0;
              return (
                <button key={idx} className="dayc" disabled={past} onClick={() => onSelect(idx)} data-strip-day={ymdOf(idx)} style={{
                  opacity: past ? .38 : 1, cursor: past ? 'not-allowed' : 'pointer',
                  borderColor: isSel ? 'var(--acc-graph)' : isToday ? 'var(--line-strong)' : 'var(--line)',
                  background: isSel ? 'var(--acc-tint)' : 'var(--surface-2)' }}>
                  <span className="mono" style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: '.06em', color: isSel ? 'var(--acc)' : n.wi === 6 ? 'var(--crit)' : n.wi === 5 ? 'var(--info)' : 'var(--ink-3)' }}>{WD[n.wi]}</span>
                  <span className="mono" style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.05, color: isSel ? 'var(--acc)' : 'var(--ink)', textDecoration: past ? 'line-through' : 'none' }}>{n.d}</span>
                  {n.d === 1
                    ? <span className="mono" style={{ fontSize: 8.5, fontWeight: 600, color: 'var(--info)' }}>{n.m}월</span>
                    : <span style={{ height: 5, display: 'flex', gap: 2, alignItems: 'center' }}>
                        {!past && cnt > 0 && <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--acc-graph)' }} />}
                        {!past && cnt > 2 && <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--line-strong)' }} />}
                      </span>}
                  {isToday && !isSel && <span className="mono" style={{ position: 'absolute', top: 3, right: 4, fontSize: 7.5, fontWeight: 700, letterSpacing: '.06em', color: 'var(--acc)' }}>TODAY</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Panel>
  );
}

/**
 * 시작·끝 선택지 — 그 설비의 그날 시간대·30분 단위·최대 길이 안(오늘은 지금 시간 칸부터). 00:00–24:00 설비는 끝 날짜를 고를 수 있다
 * (여러 날 — 그 사이 날도 이용 요일이어야 한다). keepStart는 바꾸기 창에서 진행 중인 회의의 지금 시작(옮기지 않으면 그대로 둔다). 최종 판정은 서버.
 */
function useSlotChoice(room, dayIdx, init) {
  const info = dinfo(dayIdx);
  const [win0, win1] = windowOf(room, info.wi) ?? [room.open, room.close];
  const nowFloor = dayIdx === todayIdx() ? Math.floor(nowHour()) : -1;
  const starts = [];
  for (let h = Math.max(win0, Math.ceil(nowFloor)); h < win1; h += 0.5) starts.push(h);
  if (init.keepStart !== undefined && !starts.includes(init.keepStart)) starts.unshift(init.keepStart);
  const [startPick, setStart] = uS(init.start);
  const start = starts.includes(startPick) ? startPick : starts[0] ?? win0;
  const [endIdxPick, setEndIdx] = uS(init.endIdx ?? dayIdx);
  const endIdx = Math.max(dayIdx, endIdxPick);
  const maxH = room.maxMinutes === null ? Infinity : room.maxMinutes / 60;
  const ends = [];
  for (let h = endIdx === dayIdx ? start + 0.5 : 0.5; h <= (endIdx === dayIdx ? win1 : 24); h += 0.5) {
    if (endIdx === dayIdx && h - start > maxH) break;
    ends.push(h);
  }
  const [endPick, setEnd] = uS(init.end ?? Math.min(start + 1, win1));
  const end = ends.includes(endPick) ? endPick : ends[Math.min(1, ends.length - 1)] ?? start + 0.5;
  const lengthH = (endIdx - dayIdx) * 24 + end - start;
  return { starts, ends, start, setStart, endIdx, setEndIdx, end, setEnd, lengthH, tooLong: lengthH > maxH, multi: allDay(room) };
}

/** 시작·(끝 날짜)·끝 칸 — single이면 끝 날짜를 고르지 않는다(반복 예약의 회차는 하루 안) */
function TimeFields({ dayIdx, slot, single }) {
  return (
    <React.Fragment>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>시작</Lbl>
        <select style={box} value={slot.start} onChange={e => slot.setStart(Number(e.target.value))} aria-label="시작 시각">
          {slot.starts.map(h => <option key={h} value={h}>{fmtH(h)}</option>)}
        </select></label>
      {slot.multi && !single && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>끝 날짜</Lbl>
          <input type="date" style={box} value={ymdOf(slot.endIdx)} min={ymdOf(dayIdx)} aria-label="끝 날짜"
            onChange={e => e.target.value && slot.setEndIdx(Math.max(dayIdx, idxOf(e.target.value)))} /></label>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>끝</Lbl>
        <select style={box} value={slot.end} onChange={e => slot.setEnd(Number(e.target.value))} aria-label="끝 시각">
          {slot.ends.map(h => <option key={h} value={h}>{fmtH(h)}</option>)}
        </select></label>
    </React.Fragment>
  );
}

/* ---- 반복 예약(2-3d) ---- */
const MAX_REPEAT = 52;
const md = ymd => { const [, m, d] = ymd.split('-').map(Number); return `${m}/${d}`; };
const hm = T.hm;

/**
 * 반복 미리보기 — 서버 규칙(rules.expandSeries)과 같은 계산으로 회차 날짜를 펼친다(매주는 고른 요일, 끝은 횟수 2~52 또는 끝 날짜 ≤ 1년 뒤 같은 날).
 * 보여 주기만 한다 — 회차마다의 이용 시간·겹침과 최종 판정은 서버
 */
function repeatPreview(dayIdx, rep) {
  const f = dinfo(dayIdx), first = ymdOf(dayIdx);
  const limit = `${f.y + 1}-${pad(f.m)}-${pad(Math.min(f.d, T.daysInMonth(f.y + 1, f.m)))}`;
  if (rep.end === 'count' && !(Number.isInteger(rep.count) && rep.count >= 2 && rep.count <= MAX_REPEAT)) return { problem: `횟수는 2~${MAX_REPEAT}회입니다` };
  if (rep.end === 'until' && !(rep.until > first && rep.until <= limit)) return { problem: `끝 날짜는 ${md(ymdOf(dayIdx + 1))}부터 ${f.y + 1}년 ${md(limit)}까지입니다` };
  const last = rep.end === 'until' ? rep.until : limit;
  const dates = [];
  for (let i = dayIdx; ymdOf(i) <= last; i++) {
    if (rep.freq === 'daily' || rep.weekdays.includes(dinfo(i).wi + 1)) dates.push(ymdOf(i));
    if (rep.end === 'count' && dates.length === rep.count) break;
    if (dates.length > MAX_REPEAT) return { problem: `반복은 ${MAX_REPEAT}회까지입니다 — 끝 날짜를 앞당기세요` };
  }
  if (dates.length < 2) return { problem: '반복은 2회 이상이어야 합니다 — 끝 날짜를 늘리세요' };
  return { dates };
}

/** 반복 칸 — 안 함·매일·매주(요일 칩 — 시작 날짜의 요일은 빠질 수 없다), 끝은 횟수 또는 날짜 */
function RepeatFields({ dayIdx, rep, setRep, preview }) {
  const startDay = dinfo(dayIdx).wi + 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-repeat>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Lbl>반복</Lbl>
        <SegmentedControl size="sm" value={rep.freq} onChange={v => setRep(r => ({ ...r, freq: v }))} options={[{ value: 'none', label: '안 함' }, { value: 'daily', label: '매일' }, { value: 'weekly', label: '매주' }]} />
      </div>
      {rep.freq === 'weekly' && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }} data-repeat-weekdays>
          {WD.map((w, i) => {
            const d = i + 1, on = rep.weekdays.includes(d), fixed = d === startDay;
            return <Checkbox key={d} size="sm" label={w} checked={on} disabled={fixed} data-weekday={d}
              title={fixed ? '시작 날짜의 요일은 빠질 수 없습니다' : undefined}
              onChange={() => setRep(r => ({ ...r, weekdays: on ? r.weekdays.filter(x => x !== d) : [...r.weekdays, d].sort((a, b) => a - b) }))} />;
          })}
        </div>
      )}
      {rep.freq !== 'none' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Lbl>끝</Lbl>
          <SegmentedControl size="sm" value={rep.end} onChange={v => setRep(r => ({ ...r, end: v }))} options={[{ value: 'count', label: '횟수' }, { value: 'until', label: '날짜' }]} />
          {rep.end === 'count'
            ? <input type="number" style={{ ...box, width: 72 }} min={2} max={MAX_REPEAT} value={rep.count} aria-label="반복 횟수"
                onChange={e => setRep(r => ({ ...r, count: Number(e.target.value) }))} />
            : <input type="date" style={box} min={ymdOf(dayIdx + 1)} value={rep.until} aria-label="반복 끝 날짜"
                onChange={e => setRep(r => ({ ...r, until: e.target.value }))} />}
          {preview && !preview.problem && <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }} data-repeat-preview={preview.dates.length}>
            {preview.dates.length}회 · {md(preview.dates[0])} ~ {md(preview.dates[preview.dates.length - 1])}</span>}
        </div>
      )}
      {preview?.problem && <div className="mono" style={{ fontSize: 11, color: 'var(--crit)' }}>{preview.problem}</div>}
    </div>
  );
}

/** 겹치는 회차 — 서버가 준 목록(어느 날·무엇과). 하나라도 있으면 아무것도 예약되지 않았다 */
function Conflicts({ list }) {
  const days = new Set(list.map(c => c.date)).size;
  return (
    <div data-conflicts={days} style={{ border: '1px solid var(--crit)', borderRadius: 'var(--r-ctl)', padding: '9px 11px', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <b style={{ color: 'var(--crit)' }}>겹치는 회차 {days}개 — 아무것도 예약하지 않았습니다</b>
      {list.map((c, i) => (
        <span key={i} className="mono" data-conflict={c.date} style={{ fontSize: 11.5 }}>
          {md(c.date)}({WD[(T.parts(c.startsAt).dow + 6) % 7]}) {hm(c.startsAt)}–{hm(c.endsAt)} · {c.with.title} — {c.with.requesterName}
          {c.with.status === 'pending_approval' ? ' · 승인 대기' : ''} ({hm(c.with.startsAt)}–{hm(c.with.endsAt)})
        </span>))}
    </div>
  );
}

/** 예약 창 — 반복을 고르면 반복 예약(묶음)으로 */
function BookingModal({ room, dayIdx, defaultStart, onClose, onConfirm, onToast }) {
  const info = dinfo(dayIdx);
  const slot = useSlotChoice(room, dayIdx, { start: defaultStart });
  const [title, setTitle] = uS('');
  const [attendees, setAttendees] = uS([]);
  const [rep, setRep] = uS({ freq: 'none', weekdays: [info.wi + 1], end: 'count', count: 4, until: ymdOf(dayIdx + 28) });
  const [conflicts, setConflicts] = uS(null);
  const [busy, setBusy] = uS(false);
  const repeating = rep.freq !== 'none';
  const preview = repeating ? repeatPreview(dayIdx, rep) : null;
  // 반복 예약의 회차는 하루 안 — 반복을 고르면 끝 날짜는 시작 날짜로(setEndIdx는 상태 설정 함수라 바뀌지 않는다)
  const { setEndIdx } = slot;
  uE(() => { if (repeating) setEndIdx(dayIdx); }, [repeating, dayIdx, setEndIdx]);
  const { start, end } = slot;
  const endIdx = repeating ? dayIdx : slot.endIdx;
  // 정원 미리보기(예약자 포함) — 서버가 최종 판정한다. 정원이 없는 설비(장비 등)는 검사하지 않는다
  const headcount = attendees.length + 1;
  const over = room.cap > 0 && headcount > room.cap;
  const endInfo = dinfo(endIdx);
  const submit = () => {
    setBusy(true); setConflicts(null);
    const repeat = !repeating ? null : {
      freq: rep.freq, ...(rep.freq === 'weekly' ? { weekdays: rep.weekdays } : {}), ...(rep.end === 'count' ? { count: rep.count } : { until: rep.until })
    };
    onConfirm({ room: room.id, startsAt: isoAt(dayIdx, start), endsAt: isoAt(endIdx, end), title: title.trim() || '(제목 없음)', attendees, repeat })
      .catch(e => { if (e.details?.conflicts) setConflicts(e.details.conflicts); else onToast(e.message); })
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={470} title="예약" icon="calendar" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
        <Button variant="primary" size="sm" disabled={busy || over || slot.tooLong || !slot.starts.length || slot.lengthH <= 0 || Boolean(preview?.problem)}
          onClick={submit}>{repeating && preview?.dates ? `${preview.dates.length}회 예약` : '예약'}</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }} data-booking-modal>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', background: 'var(--surface-2)' }}>
          <Icon name="building" size={16} color="var(--acc)" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{room.name}</div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
              {room.type}{room.cap ? ` · ${room.cap}인` : ''}{room.loc ? ` · ${room.loc}` : ''} · {room.openTime}–{room.closeTime}{room.maxMinutes ? ` · 최대 ${durationText(room.maxMinutes)}` : ''}
            </div>
          </div>
          <span className="mono" style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{info.m}/{info.d} ({WD[info.wi]})</span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}><TimeFields dayIdx={dayIdx} slot={slot} single={repeating} /></div>
        <RepeatFields dayIdx={dayIdx} rep={rep} setRep={setRep} preview={preview} />
        <Field label="제목" value={title} onChange={e => setTitle(e.target.value)} placeholder="예: 주간 동기화" maxLength={200} />
        <PeoplePicker value={attendees} onChange={setAttendees} />
        {conflicts && <Conflicts list={conflicts} />}
        {room.cap > 0 && <div className="mono" style={{ fontSize: 11, color: over ? 'var(--crit)' : 'var(--ink-3)' }}>정원 {room.cap}명 · 참석 {headcount}명(예약자 포함){over ? ' — 정원을 넘습니다' : ''}</div>}
        {slot.tooLong && <div className="mono" style={{ fontSize: 11, color: 'var(--crit)' }}>이 설비는 한 번에 {durationText(room.maxMinutes)}까지 예약할 수 있습니다</div>}
        {room.approvalRequired && <div style={{ fontSize: 11.5, color: 'var(--warn)' }} data-approval-note>이 설비는 담당자 승인 뒤 확정됩니다 — 참석자 초대(알림·Outlook)는 승인 뒤에 갑니다. 담당자·설비 관리자가 예약하면 바로 확정됩니다.</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--ink-3)', flexWrap: 'wrap' }}>
          <Icon name="clock" size={13} />
          <span className="mono">{fmtH(start)}–{endIdx === dayIdx ? fmtH(end) : `${endInfo.m}/${endInfo.d} ${fmtH(end)}`}</span> · 예약자 {ME.name}
          {attendees.length > 0 && !room.approvalRequired && <span>· 참석자에게 Teams 알림과 Outlook {repeating ? '반복 일정 ' : ''}초대가 갑니다</span>}
        </div>
      </div>
    </Modal>
  );
}

/** 앱 시간대 달력의 그날 0시부터 잰 소수 시각과 그날 번호(idx) — 끝이 자정이면 앞날의 24:00으로 */
const placeOf = (iso, isEnd) => {
  const idx = idxOf(T.ymd(Date.parse(iso) - (isEnd ? 1 : 0)));
  return { idx, h: (Date.parse(iso) - Date.parse(isoAt(idx, 0))) / 3_600_000 };
};

/**
 * 바꾸기 창(2-3c) — 제목·참석자는 언제나, 시간·설비는 서버가 actions.changeTime을 준 예약만(승인을 거친 예약은 취소하고 다시 신청).
 * 바꾼 값만 보낸다. 설비 목록은 승인이 필요 없는 설비(승인 필요 설비로는 옮기지 않는다 — 서버도 409).
 * 반복 예약의 회차면 '이날만 바꾸기'(2-3d) — 저장하면 그 회차는 반복에서 떨어져 단독 예약이 된다
 */
function BookingEdit({ booking, rooms, onClose, onSaved, onToast }) {
  const s0 = placeOf(booking.starts_at, false), e0 = placeOf(booking.ends_at, true);
  const [title, setTitle] = uS(booking.title);
  const [attendees, setAttendees] = uS(booking.attendees.map(a => a.id));
  const [roomId, setRoomId] = uS(booking.facility_id);
  const [dayIdx, setDayIdx] = uS(s0.idx);
  const timeOk = booking.actions?.changeTime === true;
  const inSeries = Boolean(booking.series && !booking.series.detached);
  const choices = rooms.filter(r => !r.approvalRequired || r.id === booking.facility_id);
  const room = choices.find(r => r.id === roomId) ?? rooms.find(r => r.id === roomId);
  // 진행 중인 회의는 지금 시작을 그대로 둘 수 있게(끝만 늘리기)
  const slot = useSlotChoice(room ?? { openDays: [], open: 0, close: 24, maxMinutes: null }, dayIdx,
    { start: s0.h, endIdx: e0.idx, end: e0.h, keepStart: dayIdx === s0.idx ? s0.h : undefined });
  const [busy, setBusy] = uS(false);
  const save = () => {
    const body = {};
    if (title.trim() !== booking.title) body.title = title.trim();
    const before = booking.attendees.map(a => a.id).sort().join(), after = [...attendees].sort().join();
    if (before !== after) body.attendeeIds = attendees;
    if (timeOk) {
      const startsAt = isoAt(dayIdx, slot.start), endsAt = isoAt(slot.endIdx, slot.end);
      if (Date.parse(startsAt) !== Date.parse(booking.starts_at)) body.startsAt = startsAt;
      if (Date.parse(endsAt) !== Date.parse(booking.ends_at)) body.endsAt = endsAt;
      if (roomId !== booking.facility_id) body.facilityId = roomId;
    }
    if (!Object.keys(body).length) { onClose(); return; }
    setBusy(true);
    api.patch('/api/v1/bookings/' + booking.id, body)
      .then(() => { onToast('예약을 바꿨습니다'); onSaved(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={480} title={inSeries ? '이날만 바꾸기' : '예약 바꾸기'} icon="calendar" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        <Button variant="primary" size="sm" disabled={busy || !title.trim() || (timeOk && (slot.tooLong || slot.lengthH <= 0))} onClick={save}>저장</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-booking-edit={booking.id}>
        {inSeries && <div style={{ fontSize: 12, color: 'var(--warn)' }} data-detach-note>이 회차만 바꿉니다 — 저장하면 이 회차는 반복에서 떨어져 따로 관리되고,
          Outlook에서는 반복 일정의 이 회차가 취소되고 따로 초대됩니다.</div>}
        <Field label="제목" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
        {timeOk ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>설비</Lbl>
              <select style={box} value={roomId} onChange={e => setRoomId(e.target.value)} aria-label="설비">
                {choices.map(r => <option key={r.id} value={r.id}>{r.type} · {r.name}</option>)}
              </select></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>날짜</Lbl>
              <input type="date" style={box} value={ymdOf(dayIdx)} min={ymdOf(todayIdx())} aria-label="날짜"
                onChange={e => e.target.value && setDayIdx(idxOf(e.target.value))} /></label>
            <TimeFields dayIdx={dayIdx} slot={slot} />
          </div>
        ) : <div style={{ fontSize: 12, color: 'var(--ink-3)' }} data-time-locked>승인이 필요한 예약은 시간·설비를 바꿀 수 없습니다 — 바꾸려면 취소하고 다시 신청하세요.</div>}
        {timeOk && slot.tooLong && <div className="mono" style={{ fontSize: 11, color: 'var(--crit)' }}>이 설비는 한 번에 {durationText(room.maxMinutes)}까지 예약할 수 있습니다</div>}
        <PeoplePicker value={attendees} onChange={setAttendees} />
        {booking.status === 'reserved' && <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>더한 참석자는 초대, 뺀 참석자와 남은 참석자는 변경 알림을 받고 Outlook 일정도 함께 바뀝니다.</div>}
      </div>
    </Modal>
  );
}

/**
 * 반복 전체 바꾸기(2-3d) — 제목·참석자를 끝나지 않은 회차 전부에(따로 바꾼 회차·지난 회차는 그대로). 시작 값은 묶음의 지금 참석자(상세의 series.attendees).
 * 시간·설비는 회차마다 '이날만 바꾸기'로
 */
function SeriesEdit({ booking, onClose, onSaved, onToast }) {
  const s = booking.series;
  const start = (s.attendees ?? booking.attendees).map(a => a.id);
  const [title, setTitle] = uS(booking.title);
  const [attendees, setAttendees] = uS(start);
  const [busy, setBusy] = uS(false);
  const save = () => {
    const body = {};
    if (title.trim() !== booking.title) body.title = title.trim();
    if ([...start].sort().join() !== [...attendees].sort().join()) body.attendeeIds = attendees;
    if (!Object.keys(body).length) { onClose(); return; }
    setBusy(true);
    api.patch('/api/v1/bookings/series/' + s.id, body)
      .then(r => { onToast(`반복 예약 ${r.count}회를 바꿨습니다`); onSaved(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={480} title="반복 전체 바꾸기" icon="calendar" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        <Button variant="primary" size="sm" disabled={busy || !title.trim()} onClick={save}>저장</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-series-edit={s.id}>
        <Field label="제목" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
        <PeoplePicker value={attendees} onChange={setAttendees} />
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>끝나지 않은 회차 {s.remaining}개에 함께 적용합니다 — 따로 바꾼 회차와 지난 회차는 그대로입니다.
          시간·설비는 회차마다 '이날만 바꾸기'로 바꿉니다.</div>
      </div>
    </Modal>
  );
}

/** 하루 시간표 — 열 = 그날 여는 설비들의 시간대 합(정시 단위), 설비마다 시간대 밖·쉬는 요일은 이용 불가 칸 */
function DayGrid({ rooms, day, dayIdx, onPick, onOpen }) {
  const info = dinfo(dayIdx);
  const isToday = dayIdx === todayIdx();
  const windows = rooms.map(r => windowOf(r, info.wi)).filter(Boolean);
  const g0 = windows.length ? Math.floor(Math.min(...windows.map(w => w[0]))) : 0;
  const g1 = windows.length ? Math.ceil(Math.max(...windows.map(w => w[1]))) : 0;
  const nowDec = nowHour();
  // 시간표가 넓으면(24시간 설비 등) 처음 보이는 곳을 오늘은 지금 시각 앞, 다른 날은 첫 예약(없으면 시작)으로 옮긴다
  const scroller = React.useRef(null);
  const focus = isToday ? nowDec - 1 : day.length ? Math.min(...day.map(b => b.start)) : g0;
  uE(() => { if (scroller.current) scroller.current.scrollLeft = Math.max(0, (Math.max(focus, g0) - g0) * COL_W); }, [dayIdx, g0, focus]);
  if (!rooms.length) return <Empty icon="building" title="이 분류에 설비가 없습니다" />;
  if (!windows.length) return <Empty icon="calendar" title="이 날 이용할 수 있는 설비가 없습니다" sub={`${info.m}월 ${info.d}일(${WD[info.wi]})은 이 분류 설비가 모두 쉬는 날입니다`} />;
  const hours = Array.from({ length: g1 - g0 }, (_, i) => g0 + i);
  const x = h => (Math.min(Math.max(h, g0), g1) - g0) * COL_W;
  return (
    <div ref={scroller} style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 10 }}>
      <div style={{ minWidth: LABEL_W + hours.length * COL_W, position: 'relative' }} data-day-grid={ymdOf(dayIdx)} data-grid-hours={`${fmtH(g0)}-${fmtH(g1)}`}>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--line)' }}>
          <div style={{ width: LABEL_W, flex: 'none', borderRight: '1px solid var(--line)', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 2 }} />
          {hours.map(h => <div key={h} className="mono" style={{ width: COL_W, flex: 'none', padding: '8px 0', textAlign: 'center', fontSize: 10.5, color: 'var(--ink-3)' }}>{fmtH(h)}</div>)}
        </div>
        {rooms.map(room => {
          const w = windowOf(room, info.wi);
          const books = day.filter(b => b.room === room.id);
          // 이용 불가 구간 — 쉬는 요일이면 전체, 아니면 시간대 앞뒤
          const closed = !w ? [[g0, g1]] : [[g0, w[0]], [w[1], g1]].filter(([a, b]) => b > a);
          return (
            <div key={room.id} style={{ display: 'flex', borderBottom: '1px solid var(--line)', minHeight: 52 }} data-room={room.id}>
              <div style={{ width: LABEL_W, flex: 'none', borderRight: '1px solid var(--line)', padding: '9px 12px', background: 'var(--surface-2)', position: 'sticky', left: 0, zIndex: 2 }}
                title={`${room.name}${room.loc ? ' · ' + room.loc : ''} · 이용 ${room.openTime}–${room.closeTime}${room.maxMinutes ? ' · 최대 ' + durationText(room.maxMinutes) : ''}`}>
                <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{room.name}</div>
                <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {w ? [room.cap ? `${room.cap}인` : '', room.loc].filter(Boolean).join(' · ') : '쉬는 날'}
                </div>
              </div>
              <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
                {hours.map(h => {
                  const past = isToday && h < Math.floor(nowHour());
                  const outside = !w || h + 1 <= w[0] || h >= w[1];
                  const full = books.some(b => b.start <= h && b.end >= h + 1);
                  const off = past || outside || full;
                  return <button key={h} className={off ? '' : 'slot'} disabled={off} aria-label={`${room.name} ${fmtH(h)} 예약`}
                    onClick={() => onPick(room, Math.max(h, w[0]))}
                    style={{ width: COL_W, flex: 'none', borderRight: '1px solid var(--line)', cursor: off ? 'default' : 'pointer', background: past ? 'var(--hover)' : 'transparent' }} />;
                })}
                {closed.map(([a, b]) => (
                  <div key={a} data-closed={`${fmtH(a)}-${fmtH(b)}`} style={{ position: 'absolute', left: x(a), width: x(b) - x(a), top: 0, bottom: 0, pointerEvents: 'none',
                    background: 'repeating-linear-gradient(135deg, var(--hover) 0 6px, transparent 6px 12px)' }}>
                    {!w && <span style={{ position: 'absolute', left: 10, top: 16, fontSize: 11, color: 'var(--ink-3)' }}>이용 불가</span>}
                  </div>
                ))}
                {books.map(b => {
                  const left = x(b.start), width = x(b.end) - x(b.start);
                  if (width <= 0) return null;
                  const pending = b.status === 'pending_approval';
                  return (
                    <button key={b.id} data-booking={b.id} data-mine={b.mine} data-status={b.status} data-series={b.series || undefined} onClick={() => onOpen(b.id)}
                      title={b.title + ' · ' + b.by + (b.series ? ' · 반복' : '') + (pending ? ' · 승인 대기' : '') + (b.attendees?.length ? ' · 참석 ' + b.attendees.join(', ') : '')} style={{
                        position: 'absolute', left: left + 3, width: width - 6, top: 6, bottom: 6, textAlign: 'left', cursor: 'pointer',
                        background: b.mine ? 'var(--acc-tint)' : 'var(--surface-2)',
                        border: '1px ' + (pending ? 'dashed ' : 'solid ') + (b.mine ? 'var(--acc-graph)' : 'var(--line-strong)'),
                        borderLeft: '2px ' + (pending ? 'dashed ' : 'solid ') + (b.mine ? 'var(--acc-graph)' : 'var(--line-strong)'),
                        borderRadius: 'var(--r-sm)', padding: '4px 8px', overflow: 'hidden', zIndex: 1 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: b.mine ? 'var(--acc)' : 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {b.series && <span style={{ marginRight: 4, verticalAlign: '-1px' }}><Icon name="refresh-cw" size={10} color="var(--ink-3)" /></span>}
                        {pending && <span style={{ color: 'var(--warn)', fontWeight: 700 }}>승인 대기 · </span>}{b.title}</div>
                      <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {b.by}{b.attendees?.length ? ` 외 ${b.attendees.length}` : ''} · {b.before ? '앞날부터' : fmtH(b.start)}–{b.after ? '다음 날까지' : fmtH(b.end)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {isToday && nowDec >= g0 && nowDec <= g1 && (
          <div style={{ position: 'absolute', left: LABEL_W + (nowDec - g0) * COL_W, top: 33, bottom: 0, width: 1, background: 'var(--crit)', pointerEvents: 'none', zIndex: 1 }}>
            <span style={{ position: 'absolute', top: -3, left: -2.5, width: 6, height: 6, borderRadius: '50%', background: 'var(--crit)' }} />
          </div>
        )}
      </div>
    </div>
  );
}

function Booking({ onToast, deepBooking }) {
  const [tab, setTab] = uS('board');
  const [rooms, setRooms] = uS(null);
  const [manage, setManage] = uS(false);
  const [canApprove, setCanApprove] = uS(false);
  const [pending, setPending] = uS(null);   // 승인 대기 목록(승인할 수 있는 사람만)
  const [detail, setDetail] = uS(null);     // 상세 창의 예약 id
  const [editing, setEditing] = uS(null);   // 바꾸기 창의 예약(상세에서 받은 값)
  const [seriesEditing, setSeriesEditing] = uS(null);   // 반복 전체 바꾸기 창의 회차(상세에서 받은 값)
  const [rev, setRev] = uS(0);
  const [cat, setCat] = uS('');
  const [selIdx, setSelIdx] = uS(todayIdx);
  const [winStart, setWinStart] = uS(() => Math.max(0, todayIdx() - 2));
  const [modal, setModal] = uS(null);
  const [day, setDay] = uS([]);          // 고른 날짜에 걸친 서버 예약
  const [counts, setCounts] = uS({});    // 주간 스트립의 날짜별 건수(걸친 날마다)

  // 설비(분류·이용 시간)와 설비 관리 권한(actions.manage — '설비 관리' 탭). 홈이 쓰는 설비 목록(ROOMS)도 같이 갱신한다
  const loadRooms = uCb(() => api.get('/api/v1/facilities')
    .then(r => {
      const list = mapRooms(r.facilities); setRooms(list); applyServerData({ rooms: list });
      setManage(r.actions?.manage === true); setCanApprove(r.actions?.approve === true);
    })
    .catch(e => { setRooms([]); onToast(e.message); }), [onToast]);
  uE(() => { void loadRooms(); }, [loadRooms]);
  // 승인 대기(서버가 승인할 수 있다고 한 사람만) — 탭 이름의 건수와 목록
  uE(() => {
    if (!canApprove) { setPending(null); return undefined; }
    let current = true;
    api.get('/api/v1/bookings/pending').then(r => { if (current) setPending(r.bookings); }).catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [canApprove, rev, onToast]);
  // 알림 링크·검색 결과(#/booking/<id>)로 열면 상세를 바로 연다
  uE(() => { if (deepBooking?.id) setDetail(deepBooking.id); }, [deepBooking]);

  // 고른 날짜의 실제 예약을 서버에서 가져온다(가짜 데이터로 채우지 않는다)
  const refresh = uCb(i =>
    api.get('/api/v1/bookings?date=' + ymdOf(i))
      .then(r => setDay(mapBookings(r.bookings, ME.id, ymdOf(i))))
      .catch(e => onToast(e.message)), [onToast]);
  uE(() => { void refresh(selIdx); }, [selIdx, refresh]);

  // 스트립 14일의 건수는 한 번에 조회 — 여러 날 예약은 걸친 날마다 센다
  uE(() => {
    api.get('/api/v1/bookings?from=' + ymdOf(winStart) + '&to=' + ymdOf(winStart + 13))
      .then(r => {
        const m = {};
        for (const b of r.bookings) {
          const first = idxOf(T.ymd(b.starts_at)), last = idxOf(T.ymd(Date.parse(b.ends_at) - 1));
          for (let i = Math.max(winStart, first); i <= Math.min(winStart + 13, last); i++) m[i] = (m[i] ?? 0) + 1;
        }
        setCounts(m);
      })
      .catch(e => { setCounts({}); onToast('날짜별 예약 건수를 받지 못했습니다 — ' + e.message); });
  }, [winStart, onToast]);

  // 처리(예약·승인·반려·취소) 뒤: 고른 날 시간표·승인 대기·내 예약을 서버에서 다시 받는다
  const changed = uCb(() => { setRev(x => x + 1); return refresh(selIdx); }, [refresh, selIdx]);
  // 중복 판정은 서버(DB 배타 제약)가 최종 결정한다 — 동시 신청도 여기서 걸린다. 승인 대기 여부도 서버 응답(status)으로.
  // 반복이면 묶음으로(겹치는 회차 목록 등 실패는 예약 창이 받아 보인다)
  const confirm = bk => {
    const body = { facilityId: bk.room, title: bk.title, startsAt: bk.startsAt, endsAt: bk.endsAt, attendeeIds: bk.attendees };
    return (bk.repeat ? api.post('/api/v1/bookings/series', { ...body, repeat: bk.repeat }) : api.post('/api/v1/bookings', body))
      .then(r => {
        setModal(null);
        const room = rooms.find(x => x.id === bk.room);
        const sp = T.parts(bk.startsAt);
        const when = `${room?.name ?? '설비'} ${sp.m}/${sp.d} ${fmtH(T.hourOf(bk.startsAt))}${bk.repeat ? `부터 반복 ${r.count}회` : ''}`;
        onToast(r.status === 'pending_approval' ? `${when} 승인 요청을 보냈습니다 — 담당자 승인 뒤 확정됩니다` : `${when} 예약 완료`);
        return changed();
      });
  };
  // 승인 대기 목록의 승인 — 반복 예약은 묶음 전체(actions.series)
  const approve = b => (b.actions.series?.approve ? api.post(`/api/v1/bookings/series/${b.series.id}/approve`, {}) : api.post(`/api/v1/bookings/${b.id}/approve`, {}))
    .then(r => {
      onToast(b.actions.series?.approve ? `반복 예약 ${r.count}회를 승인했습니다 — 참석자에게 초대가 갑니다` : '예약을 승인했습니다 — 참석자에게 초대가 갑니다');
      return changed();
    })
    .catch(e => onToast(e.message));

  const cats = rooms ? [...new Set(rooms.map(r => r.type))] : [];   // 서버 순서(설비 수가 많은 분류부터)
  const current = cats.includes(cat) ? cat : cats[0] ?? '';
  const shown = (rooms ?? []).filter(r => r.type === current);
  const info = dinfo(selIdx);
  const shownDay = day.filter(b => shown.some(r => r.id === b.room));
  return (
    <React.Fragment>
      <div className="head">
        <h1>회의실 · 설비 예약</h1>
        <div className="meta">
          {rooms && <Pill tone="neutral" dot={false}><span className="mono">설비 {rooms.length}개</span></Pill>}
        </div>
      </div>
      <PageTabs value={tab} onChange={setTab} items={[{ value: 'board', label: '예약 현황' }, { value: 'mine', label: '내 예약' },
        ...(canApprove ? [{ value: 'pending', label: '승인 대기', count: pending?.length || undefined }] : []), ...(manage ? [{ value: 'admin', label: '설비 관리' }] : [])]} />
      {tab === 'admin' && manage ? <div className="body" style={{ paddingTop: 14 }}><FacilityAdmin onToast={onToast} onChanged={loadRooms} /></div>
        : tab === 'mine' ? <div className="body" style={{ paddingTop: 14 }}><MyBookings onOpen={setDetail} onToast={onToast} rev={rev} /></div>
        : tab === 'pending' && canApprove ? <div className="body" style={{ paddingTop: 14 }}><PendingApprovals list={pending} onOpen={setDetail} onApprove={approve} /></div> : (
      <div className="body">
        <WeekStrip counts={counts} winStart={winStart} selIdx={selIdx} onSelect={setSelIdx} onShift={d => setWinStart(w => Math.max(0, w + d))} />
        {cats.length > 1 && (
          <RadioGroup orientation="horizontal" data-facility-types aria-label="설비 종류" value={current} onChange={setCat}
            options={cats.map(c => ({ value: c, label: <span data-facility-type={c}>{c}<span className="mono" style={{ color: 'var(--ink-3)', marginLeft: 4 }}>{rooms.filter(r => r.type === c).length}</span></span> }))} />
        )}
        <Panel>
          <PH title={`${info.m}월 ${info.d}일 ${WD[info.wi]}요일${current ? ` · ${current}` : ''}`}
            sub={`${shownDay.length}건 예약`}
            right={<span style={{ fontSize: 11.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>빈 시간대를 눌러 예약</span>} />
          {!rooms ? <Loading title="불러오는 중" />
            : <DayGrid rooms={shown} day={shownDay} dayIdx={selIdx} onPick={(room, start) => setModal({ room, start })} onOpen={setDetail} />}
        </Panel>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--ink-3)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><span style={{ width: 9, height: 3, borderRadius: 1, background: 'var(--acc-graph)' }} />내 예약</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><span style={{ width: 9, height: 1.5, background: 'var(--crit)' }} />현재 시각</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><span style={{ width: 9, height: 9, border: '1px dashed var(--line-strong)' }} />승인 대기</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><Icon name="refresh-cw" size={10} color="var(--ink-3)" />반복 예약</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><span style={{ width: 9, height: 9, background: 'repeating-linear-gradient(135deg, var(--hover) 0 3px, transparent 3px 6px)', border: '1px solid var(--line)' }} />이용 불가</span>
        </div>
      </div>
      )}
      {modal && <BookingModal room={modal.room} dayIdx={selIdx} defaultStart={modal.start} onClose={() => setModal(null)} onConfirm={confirm} onToast={onToast} />}
      {detail && <BookingDetail id={detail} onClose={() => setDetail(null)} onChanged={changed} onToast={onToast}
        onEdit={b => { setDetail(null); setEditing(b); }} onEditSeries={b => { setDetail(null); setSeriesEditing(b); }} />}
      {editing && rooms && <BookingEdit booking={editing} rooms={rooms} onToast={onToast} onClose={() => { setDetail(editing.id); setEditing(null); }}
        onSaved={() => { const id = editing.id; setEditing(null); void changed(); setDetail(id); }} />}
      {seriesEditing && <SeriesEdit booking={seriesEditing} onToast={onToast} onClose={() => { setDetail(seriesEditing.id); setSeriesEditing(null); }}
        onSaved={() => { const id = seriesEditing.id; setSeriesEditing(null); void changed(); setDetail(id); }} />}
    </React.Fragment>
  );
}

export { Booking };
